// @flow
import React, { useState, useMemo } from 'react';
import type { Element } from 'react';
import { FastField } from 'formik';

import compose from 'lodash/fp/compose';
import { isEmpty } from 'lodash';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';

import { StyledInput } from '@styles/inputs';
import type { Option } from '@components/Select/types';
import InputPhone from '@components/InputPhone';
import { Input, Select, ButtonRadio } from '@components';
import { CORE_DATA_PROCESSING_CONTACT, CORE_PRIVACY_AGREEMENT } from '@routes';
import type {
  Props, SelectProps, Values, SubmitForm
} from './types';

import {
  StyledItem,
  StyledInputUnit,
  StyledInputLabelDisplay,
  StyledSelectContainer,
  StyledSelect,
  StyledCheckbox,
  StyledAgreementTextContainer,
  StyledAgreementText,
  StyledLink,
  StyledSubmitButton,
  StyledSelectDownCaretIcon,
  StyledTextWrapper
} from './styles';

const mandatoryFields: Array<string> = ['name', 'company', 'email', 'phone', 'interest', 'region'];

const ContactEmployerForm = ({
  i18n, values, setFieldValue, t, loading
}: Props): Element<any> => {
  const [agreementCheck, setAgreementCheck] = useState<boolean>(false);

  const getInterestOptions = (): Array<Option> =>
    t('ContactEmployer.Interests', { returnObjects: true }).map(interest => ({
      label: interest,
      value: interest
    }));

  const interestOptions: Array<Option> = useMemo(getInterestOptions, [
    i18n.language,
    t('ContactEmployer.Interests')
  ]);

  const getRegionOptions = (): Array<Option> =>
    t('ContactEmployer.Regions', { returnObjects: true }).map(region => ({
      label: region,
      value: region
    }));

  const regionOptions: Array<Option> = useMemo(getRegionOptions, [
    i18n.language,
    t('ContactEmployer.Regions')
  ]);

  const checkEmptyField = (): boolean => mandatoryFields.some(field => isEmpty(values[field]));

  const isAnyFieldEmpty: boolean = React.useMemo(checkEmptyField, [values]);

  const SelectWrapper = (props: SelectProps): Element<any> => (
    <StyledSelectContainer>
      <StyledSelect {...props} />
      <StyledSelectDownCaretIcon icon={faCaretDown} />
    </StyledSelectContainer>
  );

  const handleAgreementChange = (): void => {
    setAgreementCheck(agreementCheck => !agreementCheck);
  };

  const handlePhoneChange = (number): void => {
    setFieldValue('phone', number);
  };

  return (
    <>
      <StyledItem forMobile={false}>
        <FastField
          label={`${t('ContactEmployer.Mr')}`}
          id="mr"
          name="title"
          component={ButtonRadio}
        />
        <FastField label={t('ContactEmployer.Mrs')} id="mrs" name="title" component={ButtonRadio} />
      </StyledItem>

      <StyledItem justifyContent="space-between">
        <StyledInputUnit>
          <StyledInputLabelDisplay htmlFor="name">
            {`${t('ContactEmployer.Name')}*`}
          </StyledInputLabelDisplay>
          <FastField name="name" component={Input} inputComponent={StyledInput} />
        </StyledInputUnit>

        <StyledInputUnit>
          <StyledInputLabelDisplay htmlFor="company">
            {`${t('ContactEmployer.Company')}*`}
          </StyledInputLabelDisplay>
          <FastField name="company" component={Input} inputComponent={StyledInput} />
        </StyledInputUnit>
      </StyledItem>

      <StyledItem justifyContent="space-between">
        <StyledInputUnit>
          <StyledInputLabelDisplay htmlFor="email">
            {`${t('ContactEmployer.Email')}*`}
          </StyledInputLabelDisplay>
          <FastField name="email" component={Input} inputComponent={StyledInput} />
        </StyledInputUnit>

        <StyledInputUnit>
          <StyledInputLabelDisplay htmlFor="phone">
            {`${t('ContactEmployer.Phone')}*`}
          </StyledInputLabelDisplay>
          <FastField
            name="phone"
            component={InputPhone}
            onChange={handlePhoneChange}
            inputComponent={StyledInput}
          />
        </StyledInputUnit>
      </StyledItem>

      <StyledItem justifyContent="space-between">
        <StyledInputUnit>
          <StyledInputLabelDisplay htmlFor="interest">
            {`${t('ContactEmployer.InterestedIn')}*`}
          </StyledInputLabelDisplay>
          <FastField
            name="interest"
            component={Select}
            inputComponent={Select}
            selectComponent={SelectWrapper}
            options={interestOptions}
            placeholder={t('ContactEmployer.Prompt')}
          />
        </StyledInputUnit>

        <StyledInputUnit>
          <StyledInputLabelDisplay htmlFor="region">
            {`${t('ContactEmployer.Region')}*`}
          </StyledInputLabelDisplay>
          <FastField
            name="region"
            component={Select}
            selectComponent={SelectWrapper}
            options={regionOptions}
            placeholder={t('ContactEmployer.Prompt')}
          />
        </StyledInputUnit>
      </StyledItem>

      <StyledItem forMobile={false}>
        <StyledCheckbox inverse checked={agreementCheck} onChange={handleAgreementChange} />
        <StyledAgreementTextContainer>
          <StyledTextWrapper addMargin>
            <StyledAgreementText asBlock>{t('ContactEmployer.AgreementText')}</StyledAgreementText>
            <StyledLink href={CORE_DATA_PROCESSING_CONTACT(i18n.language)} target="_blank">
              {t('ContactEmployer.AgreementTextLink')}
            </StyledLink>
            .
          </StyledTextWrapper>

          <StyledTextWrapper>
            <StyledAgreementText>{t('ContactEmployer.PrivacyAgreementText')}</StyledAgreementText>
            <StyledLink href={CORE_PRIVACY_AGREEMENT(i18n.language)} target="_blank">
              {t('ContactEmployer.PrivacyAgreementLink')}
            </StyledLink>
            .
          </StyledTextWrapper>
        </StyledAgreementTextContainer>
      </StyledItem>

      <StyledItem justifyContent="center">
        <StyledSubmitButton disabled={!agreementCheck || loading || isAnyFieldEmpty} type="submit">
          {t('ContactEmployer.SubmitButton')}
        </StyledSubmitButton>
      </StyledItem>
    </>
  );
};

// eslint-disable-next-line no-unused-vars
const handleSubmit = (submitForm: SubmitForm) => async (
  values: Values,
  { props }
): Promise<any> => {
  const { ...restValues } = values;
  await submitForm({
    variables: restValues
  });
  props.setFormSubmitted(true);
};

export default compose()(ContactEmployerForm);
